import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import logo from "@/assets/kultmans-logo.png";

const navItems = [
  { title: "Start", href: "/" },
  { title: "Der Verein", href: "/verein" },
  { title: "Philosophie", href: "/philosophie" },
  { title: "Eventlocation", href: "/eventlocation" },
  { title: "Angebote & Kurse", href: "/angebote" },
  { title: "Veranstaltungen", href: "/veranstaltungen" },
  { title: "Sponsoren", href: "/sponsoren" },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let frame: number | null = null;
    let last = false;
    const handleScroll = () => {
      if (frame !== null) return; // max. ein Update pro Frame
      frame = requestAnimationFrame(() => {
        frame = null;
        const next = window.scrollY > 20;
        if (next !== last) {
          last = next;
          setScrolled(next);
        }
      });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);


  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <a
        href="#hauptinhalt"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
      >
        Zum Hauptinhalt springen
      </a>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || isOpen
            ? "bg-background/95 backdrop-blur-lg border-b border-border shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 z-50 relative">
              <img 
                src={logo} 
                alt="Kultmans" 
                width={800}
                height={184}
                className="h-10 md:h-12 w-auto transition-transform duration-300 hover:scale-105" 
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
                    isActive(item.href)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.title}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-primary transition-all duration-300 rounded-full ${
                      isActive(item.href) ? "w-6" : "w-0 group-hover:w-4"
                    }`}
                  />
                </Link>
              ))}
            </nav>

            {/* Desktop CTA + Theme Toggle */}
            <div className="hidden lg:flex items-center gap-2">
              <ThemeToggle />
              <Button variant="hero" size="sm" asChild>
                <Link to="/mitglied-werden">Mitglied werden</Link>
              </Button>
            </div>

            {/* Mobile: Theme Toggle + Menu Button */}
            <div className="lg:hidden flex items-center gap-1">
              <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative z-50 w-10 h-10 flex items-center justify-center rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <div className="relative w-5 h-5">
                <span
                  className={`absolute left-0 block w-5 h-0.5 bg-foreground transition-all duration-300 ${
                    isOpen ? "top-2 rotate-45" : "top-1"
                  }`}
                />
                <span
                  className={`absolute left-0 top-2 block w-5 h-0.5 bg-foreground transition-all duration-300 ${
                    isOpen ? "opacity-0 scale-0" : "opacity-100"
                  }`}
                />
                <span
                  className={`absolute left-0 block w-5 h-0.5 bg-foreground transition-all duration-300 ${
                    isOpen ? "top-2 -rotate-45" : "top-3"
                  }`}
                />
              </div>
            </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-background/98 backdrop-blur-xl transition-opacity duration-500 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsOpen(false)}
        />

        {/* Menu Content */}
        <nav className="relative h-full flex flex-col pt-24 pb-8 px-6 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item, index) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center justify-between px-4 py-4 text-lg font-medium rounded-xl transition-all duration-300 ${
                  isActive(item.href)
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-secondary/50"
                }`}
                style={{
                  transitionDelay: isOpen ? `${index * 50}ms` : "0ms",
                  transform: isOpen ? "translateX(0)" : "translateX(-20px)",
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <span>{item.title}</span>
                <ChevronRight
                  size={20}
                  className={`transition-colors ${
                    isActive(item.href) ? "text-primary" : "text-muted-foreground"
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Mobile CTA */}
          <div
            className="mt-auto pt-8"
            style={{
              transitionDelay: isOpen ? `${navItems.length * 50}ms` : "0ms",
              transform: isOpen ? "translateY(0)" : "translateY(20px)",
              opacity: isOpen ? 1 : 0,
              transition: "all 0.3s ease-out",
            }}
          >
            <Button variant="hero" size="lg" className="w-full" asChild>
              <Link to="/mitglied-werden">
                Mitglied werden
                <ChevronRight size={20} />
              </Link>
            </Button>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Kulturmanufaktur im alten Stemke
            </p>
          </div>
        </nav>
      </div>
    </>
  );
}
